import { Injectable, OnDestroy, computed, signal } from '@angular/core';
import { type Observable, Subject } from 'rxjs';
import {
  IMPERSONATION_STORAGE_KEY,
  ImpersonationSessionResponse,
} from '@/app/features/impersonation/models/impersonation.models';

@Injectable({ providedIn: 'root' })
export class ImpersonationService implements OnDestroy {
  private readonly sessionSignal = signal<ImpersonationSessionResponse | null>(null);
  private readonly nowSignal = signal(Date.now());
  private tickHandle: ReturnType<typeof setInterval> | null = null;

  private readonly cambios = new Subject<void>();

  public readonly session = this.sessionSignal.asReadonly();

  /**
   * Cada vez que se entra a una sesión de soporte, se pasa de una a otra o se sale (también al
   * vencer). Lo escucha `SesionAlDia` para releer `/auth/me`: sin eso `currentUser` seguía siendo
   * el del admin en su cuenta interna —sus add-ons, su sesión— mientras atendía otra cuenta
   * (diseño de botones por rol, 1.5 y 5.6). Rehidratar al recargar no avisa: el primer `/auth/me`
   * ya sale con la cabecera de soporte.
   */
  public readonly transiciones: Observable<void> = this.cambios.asObservable();

  public readonly sessionId = computed(() => this.sessionSignal()?.id ?? null);
  public readonly isActive = computed(() => this.sessionSignal() !== null);

  public readonly msRemaining = computed(() => {
    const s = this.sessionSignal();
    if (!s) return 0;
    const expiresMs = Date.parse(s.expires_at);
    return Math.max(0, expiresMs - this.nowSignal());
  });

  public readonly countdownLabel = computed(() => {
    const ms = this.msRemaining();
    if (ms <= 0) return '00:00:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((totalSec % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = (totalSec % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  });

  constructor() {
    this.hydrateFromStorage();
    this.startTicking();
  }

  /**
   * El reloj de la cuenta regresiva muere con su inyector: sin esto, cada inyector raíz que se
   * destruye (cada test) dejaba un `setInterval` de 1 s corriendo para siempre (refutación rt2).
   */
  public ngOnDestroy(): void {
    if (this.tickHandle !== null) clearInterval(this.tickHandle);
    this.tickHandle = null;
  }

  public set(session: ImpersonationSessionResponse): void {
    const otra = this.sessionSignal()?.id !== session.id;
    this.sessionSignal.set(session);
    this.persist(session);
    if (otra) this.cambios.next();
  }

  public clear(): void {
    const habia = this.sessionSignal() !== null;
    this.sessionSignal.set(null);
    this.removeFromStorage();
    if (habia) this.cambios.next();
  }

  private hydrateFromStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ImpersonationSessionResponse;
      if (Date.parse(parsed.expires_at) <= Date.now()) {
        this.removeFromStorage();
        return;
      }
      this.sessionSignal.set(parsed);
    } catch {
      this.removeFromStorage();
    }
  }

  private persist(session: ImpersonationSessionResponse): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // quota / disabled storage — silently ignore
    }
  }

  private removeFromStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private startTicking(): void {
    if (typeof window === 'undefined' || this.tickHandle) return;
    this.tickHandle = setInterval(() => {
      this.nowSignal.set(Date.now());
      const s = this.sessionSignal();
      if (s && Date.parse(s.expires_at) <= Date.now()) {
        this.clear();
      }
    }, 1000);
  }
}
